import type { IsoDate } from '../dates';
import { canTakeMeatAddOn } from '../diet';
import type { Plan, PlannedDay } from '../plan';
import { productOf } from '../price-book';
import type { Recipe } from '../recipe';
import type { PlanRequest } from './request';

/**
 * DE: Abendessen eines Tages ersetzen; Fleischbeilagen bleiben nur, wenn das neue Gericht eine hat
 * und die Person sie essen darf.
 * EN: Replace a day's dinner; meat add-ons stay only if the new dinner offers one and the member
 * may eat it.
 */
export function replaceDinner(
  plan: Plan,
  day: PlannedDay,
  recipe: Recipe,
  request: PlanRequest,
): Plan {
  const addOn = recipe.meatAddOn;
  const meatAddOns = day.dinner.meatAddOns.flatMap((old) => {
    const member = request.household.members.find((candidate) => candidate.id === old.memberId);
    if (!addOn || !member) return [];
    const product = productOf(request.book.catalog, addOn.productId);
    if (!canTakeMeatAddOn(member, product)) return [];
    const quantity = addOn.quantityPerPortion * member.portionFactor * 2;
    return [{ memberId: member.id, productId: product.id, quantity }];
  });
  const dinner = { ...day.dinner, recipeId: recipe.id, meatAddOns };
  return { ...plan, days: plan.days.map((other) => (other === day ? { ...day, dinner } : other)) };
}

/**
 * DE: Abendessen eines Tages sperren oder freigeben; gesperrte Tage bleiben beim Neuplanen.
 * EN: Lock or unlock a day's dinner; locked days stay when planning again.
 */
export function setDinnerLocked(plan: Plan, date: IsoDate, locked: boolean): Plan {
  const days = plan.days.map((day) =>
    day.date === date ? { ...day, dinner: { ...day.dinner, locked } } : day,
  );
  return { ...plan, days };
}
